import React, { useRef, useState } from "react";

// Components
import { Box, Button, FormControlLabel, List, ListItem, SxProps, Theme } from "@mui/material";
import { Checkbox } from "components/Checkbox";
import theme from "assets/styles/themes";
import CustomIcon from "components/CustomIcon";
import icons from "enums/icons";

// Types
import { TCustomiseLabelsOptions, TOptionsIds, customiseLabelsOptions } from "./CustomiseLabels.config";

const CustomiseLabels = ({
    options,
    setOptions,
    collapsed = true,
    sx,
}: {
    options: TCustomiseLabelsOptions;
    setOptions: (options: TCustomiseLabelsOptions) => void;
    collapsed?: boolean;
    sx?: SxProps<Theme>;
}) => {
    const [hide, setHide] = useState(collapsed);

    // Refs needed to calculate the height the height of the customise labels panel
    const buttonRef = useRef<HTMLButtonElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    const buttonHeight = buttonRef.current?.clientHeight || 0;
    const listHeight = listRef.current?.clientHeight || 0;
    const totalHeight = buttonHeight + listHeight;

    const selectedOptions = Object.entries(options);

    const handleToggleCustomiseLabels = () => {
        setHide(!hide);
    };

    const handleCheckOption = (event: React.ChangeEvent<HTMLInputElement>, id: TOptionsIds) => {
        setOptions({ ...options, [id]: event.target.checked });
    };

    return (
        <Box
            sx={{
                width: 175,
                height: buttonHeight,
                position: "relative",
                ...sx,
            }}
        >
            <Box
                sx={{
                    width: 175,
                    overflow: "hidden",
                    position: "absolute",
                    transition: "all 250ms",
                    height: hide ? buttonHeight : totalHeight,
                    transform: `translate(0, ${hide ? 0 : `${-listHeight}px`})`,
                }}
            >
                <Box
                    sx={{
                        width: "inherit",
                        position: "absolute",
                        transition: "transform 250ms",
                        transform: `translate(0, ${hide ? `calc(100% - ${totalHeight}px)` : 0})`,
                    }}
                >
                    {/* Toggle Button */}
                    <Button
                        ref={buttonRef}
                        variant="contained"
                        onClick={handleToggleCustomiseLabels}
                        endIcon={<CustomIcon icon={hide ? icons.chevronUpThin : icons.chevronDownThin} />}
                        sx={{
                            py: 0.7,
                            px: 1.4,
                            width: "inherit",
                            minWidth: "auto",
                            height: "auto",
                            borderRadius: "20px 20px 0 0",
                        }}
                    >
                        Customise Labels
                    </Button>

                    {/* List */}
                    <List sx={{ backgroundColor: theme.palette.background.paleBlue, p: 1 }} ref={listRef}>
                        {selectedOptions.map((selectedOpt) => {
                            const [id, value] = selectedOpt;
                            const opt = customiseLabelsOptions.find(
                                (op) => op.id === id
                            ) as typeof customiseLabelsOptions[number];

                            return (
                                <ListItem key={opt.id} sx={{ px: 0, py: 0.5 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={value}
                                                onChange={(ev) => handleCheckOption(ev, id as TOptionsIds)}
                                                name={opt.label}
                                            />
                                        }
                                        label={opt.label}
                                        sx={{
                                            margin: 0,
                                            "& .MuiTypography-root": {
                                                fontSize: 10,
                                                fontWeight: 600,
                                                letterSpacing: 0.4,
                                                color: theme.palette.neutrals.dark,
                                            },
                                            "& .MuiCheckbox-root": {
                                                padding: 0,
                                                mr: 0.5,
                                            },
                                        }}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>
            </Box>
        </Box>
    );
};

export default CustomiseLabels;
